import React, { FC, useState, useRef, useCallback, type ForwardRefExoticComponent } from 'react';
import { debounce } from 'debounce';
import BEMHelper from '@ynap/bem-helper';
import { Button } from '@ynap/button';
import { useMessages, useConfig } from '@ynap/immutables-provider';
import { FindItemsEntity, FindLoqateResponse, AddressFinderParams, CheckoutAnalytics } from './@types';
import { TrackingProp } from 'react-tracking';
import { findAddress, retrieveAddress } from './utils';
import { addressSearchStarted, addressSearchResults, addressSearchNoResults, addressSearchSelected, addressSearchManually } from './utils/analytics';
import { withOptimizely } from '@ynap/optimizely-utils';

const bem = new BEMHelper('AddressFinder');

export type AddressFinderProps = {
    country: string;
    addressFinderParams: AddressFinderParams;
    tracking?: TrackingProp;
    optimizely?: {
        track: (arg1: string) => void;
    };
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
};

export type SearchEntity = {
    text?: string;
    container?: string;
};

export type HighlightResult = {
    value: string;
    bold: boolean;
};

const AddressFinder: FC<AddressFinderProps> = ({ country, addressFinderParams, tracking, optimizely, primaryCategory, checkoutAnalytics }) => {
    const { addressFinder, handleRemoveAddressFinder, handleSelectedAddressClick, addressFieldsMaxChars } = addressFinderParams;
    const messages = useMessages();
    const config = useConfig();

    const [inputValue, setInputValue] = useState<string | undefined>('');
    const [focussed, setFocus] = useState(false);
    const [foundAddresses, setFoundAddresses] = useState<FindLoqateResponse | null>(null);
    const [prevSearch, setPrevSearch] = useState<SearchEntity[]>([]);
    const [nonLatinError, setNonLatinError] = useState(false);
    const expandableArea = useRef<HTMLDivElement>(null);
    const inputField = useRef<HTMLInputElement>(null);
    const options = useRef<HTMLDivElement>(null);
    const backButton = useRef<HTMLButtonElement>(null);
    const hasFoundAddresses = foundAddresses?.Items?.length && foundAddresses.Items.length > 0 && !foundAddresses.Items[0]?.Error;

    const findAddresses = async (text?: string, resetPrevSearch = true, container?: string) => {
        const result = container
            ? await findAddress({ ...addressFinder, container, country, sort: true })
            : await findAddress({ ...addressFinder, text, country });
        resetPrevSearch && setPrevSearch([]);
        setFoundAddresses(result || null);
        tracking?.trackEvent(
            result?.Items?.length ? addressSearchResults(primaryCategory, checkoutAnalytics) : addressSearchNoResults(primaryCategory, checkoutAnalytics),
        );
    };

    const selectAddress = async (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>, address: FindItemsEntity) => {
        e.preventDefault();
        if (address.Type !== 'Address') {
            const container = address.Id;
            const containerSearch = await findAddress({ ...addressFinder, country, container, sort: true });
            const newPrevSearch =
                prevSearch.length == 0
                    ? [{ text: address.Text, container: address.Id }, { text: inputValue }, ...prevSearch]
                    : [{ text: address.Text, container: address.Id }, ...prevSearch];
            setPrevSearch(newPrevSearch);
            setInputValue(address.Text);
            inputField?.current?.focus();
            setFoundAddresses(containerSearch || null);
        } else {
            const id = address.Id;
            const retrievedAddress = await retrieveAddress({ ...addressFinder, id, addressFieldsMaxChars });
            handleSelectedAddressClick(retrievedAddress || []);
            setInputValue('');
            setFocus(false);
            setFoundAddresses(null);
            optimizely?.track('address_lookup_web00008');
            tracking?.trackEvent(addressSearchSelected(primaryCategory, checkoutAnalytics));
        }
    };

    const handleAddManualAddress = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        e.preventDefault();
        handleRemoveAddressFinder();
        setFocus(false);
        optimizely?.track('manual_add_address_web00008');
        tracking?.trackEvent(addressSearchManually(primaryCategory, checkoutAnalytics));
    };

    const handleFocusClick = () => {
        document.addEventListener('mousedown', handleBodyClick);
        setFocus(true);
        tracking?.trackEvent(addressSearchStarted(primaryCategory, checkoutAnalytics));
    };

    const handleBodyClick = (event: MouseEvent) => {
        const isClickInsideElement = expandableArea?.current?.contains(event?.target as any);
        if (!isClickInsideElement) {
            setFocus(false);
            document.removeEventListener('mousedown', handleBodyClick);
        }
    };

    const handleGoBack = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const { text, container } = prevSearch[1];
        setInputValue(text);
        findAddresses(text, false, container);
        const getTail = ([_, ...prevSearch]: SearchEntity[]) => prevSearch; // remove 1st item from array
        prevSearch.length > 0 && setPrevSearch(getTail(prevSearch));
    };

    const handleButtonNavigation = (e: React.KeyboardEvent<HTMLButtonElement>, address?: FindItemsEntity) => {
        const eventKey = e.key;
        e.preventDefault();
        const target = e.target as HTMLButtonElement | undefined;
        const backBtn = backButton?.current;
        if (['Enter', 'Space'].includes(eventKey)) address ? selectAddress(e, address) : e.target == backBtn ? handleGoBack(e) : handleAddManualAddress(e);
        if (['Enter', 'Space', 'Escape'].includes(eventKey)) inputField?.current?.focus();

        if (eventKey === 'ArrowUp') {
            const lastEl = options?.current?.lastChild;
            const prevEl = target?.previousElementSibling;
            const prevButton = (target == backBtn ? lastEl : prevEl || backBtn || lastEl) as HTMLElement;
            prevButton && prevButton.focus();
        }

        if (eventKey === 'ArrowDown') {
            const firstEl = options?.current?.firstChild;
            const nextEl = target?.nextElementSibling;
            const nextButton = (target == backBtn ? firstEl : nextEl || backBtn || firstEl) as HTMLElement;
            nextButton && nextButton.focus();
        }
    };

    const handleInputNavigation = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const eventKey = e.key;
        ['Enter', 'ArrowDown', 'ArrowUp'].includes(eventKey) && e.preventDefault();

        if (eventKey === 'Tab') setFocus(false);

        if ((e.target as HTMLInputElement)?.value != '') {
            const lastEl = options?.current?.lastChild as HTMLElement;

            if (['Enter', 'ArrowDown'].includes(eventKey)) {
                const firstEl = options?.current?.firstChild as HTMLElement;

                const backBtn = backButton?.current;
                backBtn ? backBtn.focus() : firstEl && firstEl.focus();
            }

            if (eventKey === 'ArrowUp') lastEl?.focus();
        }
    };

    const highlight = (text: string, ranges: string[]): HighlightResult[] => {
        if (!ranges) return [{ value: text, bold: false }];

        const elements: HighlightResult[] = [];
        let startIndex = 0;

        ranges.forEach((range, index) => {
            const [i1, i2] = range.split('-');
            elements.push({ value: text.slice(startIndex, parseInt(i1)), bold: false });
            elements.push({ value: text.slice(parseInt(i1), parseInt(i2)), bold: true });
            if (index == ranges.length - 1) elements.push({ value: text.slice(parseInt(i2), text.length), bold: false });
            startIndex = parseInt(i2);
        });

        return elements;
    };

    const debouncedFindAddress = useCallback(
        debounce((value: string) => findAddresses(value), config?.head?.loqate?.debounceDelay || 100),
        [country],
    );

    const validateAddressField = (value: string) => {
        setNonLatinError(false);
        // 1st char Latin alphanumeric + set of German/French specific characters (Taken from WAPI field validation)
        // rest of the characters can also include comma. First character as a comma is not valid as a request parameter
        const valid = value.match(/^[A-Za-z0-9 \u00A0-\u00FF][A-Za-z0-9 ,\u00A0-\u00FF]*$/g);
        if (value === '' || valid) {
            setInputValue(value);
            debouncedFindAddress(value);
        }
        if (value !== '' && !valid) setNonLatinError(true);
    };

    return (
        <>
            <div className={bem('wrapper')}>
                <div className={bem('inputField', focussed ? 'focussed' : undefined)} ref={expandableArea} onFocus={handleFocusClick}>
                    <input
                        className={bem('input')}
                        type="search"
                        value={inputValue}
                        onKeyDown={handleInputNavigation}
                        onChange={(e) => {
                            validateAddressField(e.target.value);
                        }}
                        ref={inputField}
                    />
                    {focussed && (
                        <div className={bem('dropdown')}>
                            <div className={bem('tooltip')}>
                                {prevSearch.length > 1 ? (
                                    <button className={bem('goBackButton')} ref={backButton} onKeyDown={handleButtonNavigation} onClick={handleGoBack}>
                                        <span className={bem('goBackArrow')} />
                                        {messages.addressForm?.addressFinder?.goBack?.()}
                                    </button>
                                ) : (
                                    <span>
                                        {nonLatinError
                                            ? messages.addressForm?.addressFinder?.nonLatinError?.()
                                            : messages.addressForm?.addressFinder?.keepTyping?.()}
                                    </span>
                                )}
                            </div>
                            {hasFoundAddresses && (
                                <div className={bem('options')} ref={options}>
                                    {foundAddresses?.Items.map((address: FindItemsEntity) => {
                                        const expandable = address.Type !== 'Address';

                                        const textHighlighter = address.Highlight.split(';')[0]?.split(',');
                                        const descriptionHighlighter = address.Highlight.split(';')[1]?.split(',');
                                        const text = highlight(address.Text, textHighlighter);
                                        const description = highlight(address.Description, descriptionHighlighter);
                                        const highlightedValue = [...text, { value: description.length > 0 ? ', ' : '', bold: false }, ...description];
                                        return (
                                            <button
                                                key={address.Id}
                                                className={bem('option')}
                                                onKeyDown={(e) => handleButtonNavigation(e, address)}
                                                onClick={(e) => selectAddress(e, address)}
                                            >
                                                <span key={address.Id}>
                                                    {highlightedValue.length > 0 &&
                                                        highlightedValue.map(({ value, bold }: HighlightResult, index) =>
                                                            bold ? <b key={index}>{value}</b> : value,
                                                        )}
                                                </span>
                                                {expandable && <span className={bem('expandableArrow')} />}
                                            </button>
                                        );
                                    })}
                                    <button className={bem('option')} onKeyDown={handleButtonNavigation} onClick={handleAddManualAddress}>
                                        <span>{messages.addressForm?.addressFinder?.enterManually?.()}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Button onClick={handleAddManualAddress} type="primary-link" className={bem('manualAddressCTA')}>
                {messages?.addressForm?.addressFinder?.enterManually()}
            </Button>
        </>
    );
};

// TODO withOptimizely needs typing
export default withOptimizely(AddressFinder) as ForwardRefExoticComponent<AddressFinderProps>;
